import React from "react";
import { navigate } from "gatsby";
import { I18nextProvider } from "react-i18next";
import {
  DEFAULT_I18N_CONTEXT,
  I18nContext,
  I18nPageContext,
  LANGUAGE_KEY,
} from "./context";
import { createI18nInstance, LocaleEdges } from "./createInstance";
import { localePath, stripLocale } from "../utils/locale";

interface PageProps {
  data?: { locales?: LocaleEdges };
  pageContext: { i18n?: I18nPageContext };
  location: { pathname: string; search: string; hash: string };
}

interface WrapPageElementArgs {
  element: React.ReactNode;
  props: PageProps;
}

/** Shared by gatsby-browser and gatsby-ssr. Visitors who picked a language
    with the selector are sent to it on unrouted pages; there is deliberately
    no browser-language detection. */
export const wrapPageElement = ({ element, props }: WrapPageElementArgs) => {
  const { data, pageContext, location } = props;
  const context = pageContext.i18n ?? DEFAULT_I18N_CONTEXT;

  if (!context.routed && typeof window !== "undefined") {
    const stored = window.localStorage.getItem(LANGUAGE_KEY);
    if (
      stored &&
      stored !== context.defaultLanguage &&
      context.languages.includes(stored)
    ) {
      const to = localePath(stripLocale(location.pathname), stored);
      if (to !== location.pathname) {
        navigate(to + location.search + location.hash, { replace: true });
        return null;
      }
    }
  }

  const instance = createI18nInstance(context.language, data?.locales);
  return (
    <I18nextProvider i18n={instance}>
      <I18nContext.Provider value={context}>{element}</I18nContext.Provider>
    </I18nextProvider>
  );
};
